import { useEffect, useState } from "react";
import api from "@/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiErrorMessage } from "@/utils/apiError";
import { isTrialClosed, openTasks } from "@/utils/trialClose";

/**
 * Closes or reopens a trial, always after a confirmation (utils/trialClose.js): closing shows how many tasks are
 * still open (no done date, not cancelled) and lists them; reopening says it shows on the overview and dashboard
 * again. With `askFor` (e.g. "Final Report"), it first asks "<askFor> is completed. Close the trial now?" - Yes
 * goes on to the confirmation. Saved with POST /trials/{id}/update { Closed }; `onChanged(closed)` after.
 * `trial` needs its diaries for the overview.
 */
export function CloseTrialDialog({ trial, open, onOpenChange, askFor = null, onChanged }) {
  const closed = isTrialClosed(trial);
  const [step, setStep] = useState("confirm");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setStep(askFor && !closed ? "ask" : "confirm");
      setError(null);
    }
  }, [open, askFor, closed]);

  const remaining = openTasks(trial?.diaries);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/trials/${trial.id}/update`, { Closed: !closed });
      onOpenChange(false);
      if (onChanged) onChanged(!closed);
    } catch (err) {
      setError(apiErrorMessage(err, closed ? "Failed to reopen the trial." : "Failed to close the trial."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="close-trial-dialog">
        {step === "ask" ? (
          <>
            <DialogHeader>
              <DialogTitle>Close the trial?</DialogTitle>
              <DialogDescription>{askFor} is completed. Close the trial now?</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Not now</Button>
              <Button onClick={() => setStep("confirm")}>Yes</Button>
            </DialogFooter>
          </>
        ) : closed ? (
          <>
            <DialogHeader>
              <DialogTitle>Reopen trial {trial?.trialno}?</DialogTitle>
              <DialogDescription>The trial is shown on the trial overview and the dashboard again.</DialogDescription>
            </DialogHeader>
            {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving}>Reopen trial</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Close trial {trial?.trialno}?</DialogTitle>
              <DialogDescription>
                A closed trial is hidden from the trial overview and the dashboard. It can be reopened.
              </DialogDescription>
            </DialogHeader>
            <div data-testid="close-trial-open-tasks">
              <p className="text-sm font-medium">
                {remaining.length === 0 ? "No open tasks." : `${remaining.length} open ${remaining.length === 1 ? "task" : "tasks"} (no done date):`}
              </p>
              {remaining.length > 0 && (
                <ul className="mt-1 max-h-48 list-disc overflow-y-auto pl-5 text-sm text-gray-700 dark:text-gray-300">
                  {remaining.map((task) => (
                    <li key={task.id}>{task.task || task.vano}</li>
                  ))}
                </ul>
              )}
            </div>
            {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving}>Close trial</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
